<template>
  <div class="zm-table"
       role="application"
       :class="[{
      'zm-table--fit': fit,
      'zm-table--striped': stripe,
      'zm-table--border': border || isGroup,
      'zm-table--hidden': isHidden,
      'zm-table--group': isGroup,
      'zm-table--fluid-height': maxHeight,
      'zm-table--scrollable-x': layout.scrollX,
      'zm-table--scrollable-y': layout.scrollY,
      'zm-table--enable-row-hover': !store.states.isComplex,
      'zm-table--enable-row-transition': (store.states.data || []).length !== 0 && (store.states.data || []).length < 100
    }, tableSize ? `zm-table--${ tableSize }` : '']"
       @mouseleave="handleMouseLeave">
    <div class="hidden-columns" ref="hiddenColumns">
      <slot></slot>
    </div>
    <template v-if="stickyHeader">
      <zm-sticky
        v-if="showNotFixedHeader"
        enabled
        check-position="top"
        append-cls="zm-table"
        :max-top="stickyMaxTop"
        :offset="stickyHeader.offset || 0"
        @sticky-change="onStickyChange"
        ref="stickyHeader"
      >
        <div
          v-mousewheel="handleHeaderFooterMousewheel"
          class="zm-table__header-wrapper"
          ref="headerWrapper">
          <table-header
            ref="tableHeader"
            :store="store"
            :border="border"
            :default-sort="defaultSort"
            :style="{
          width: headWidth
        }">
          </table-header>
        </div>
      </zm-sticky>
    </template>
    <template v-else>
      <div
        v-if="showNotFixedHeader"
        v-mousewheel="handleHeaderFooterMousewheel"
        class="zm-table__header-wrapper"
        ref="headerWrapper">
        <table-header
          ref="tableHeader"
          :store="store"
          :border="border"
          :default-sort="defaultSort"
          :style="{
          width: headWidth
        }">
        </table-header>
      </div>
    </template>
    <div
      class="zm-table__body-wrapper"
      ref="bodyWrapper"
      :class="[layout.scrollX ? `is-scrolling-${scrollPosition}` : 'is-scrolling-none']"
    >
      <zm-scrollbar
        ref="bar"
        @scroll="syncPosition"
        :sticky="stickyBar"
        :wrap-style="[bodyHeight]"
      >
        <table-body
          :context="context"
          :store="store"
          :stripe="stripe"
          :row-class-name="rowClassName"
          :row-style="rowStyle"
          :style="{
           width: bodyWidth
        }">
        </table-body>
        <div
          v-if="!data || data.length === 0"
          class="zm-table__empty-block"
          ref="emptyBlock"
        >
        <span class="zm-table__empty-text">
          <slot name="empty">{{ emptyText || t('table.emptyText') }}</slot>
        </span>
        </div>
        <div
          v-if="$slots.append"
          class="zm-table__append-wrapper"
          ref="appendWrapper">
          <slot name="append"></slot>
        </div>
      </zm-scrollbar>
    </div>
    <div
      v-if="showSummary"
      v-show="data && data.length > 0"
      v-mousewheel="handleHeaderFooterMousewheel"
      class="zm-table__footer-wrapper"
      ref="footerWrapper">
      <table-footer
        :store="store"
        :border="border"
        :sum-text="sumText || t('table.sumText')"
        :summary-method="summaryMethod"
        :default-sort="defaultSort"
        :style="{
          width: headWidth
        }">
      </table-footer>
    </div>
    <div
      v-if="showFixedLeft"
      v-mousewheel="handleFixedMousewheel"
      @mouseenter="onEnterFixedPanel"
      @mouseleave="onLeaveFixedPanel"
      class="zm-table__fixed"
      ref="fixedWrapper"
    >
      <template v-if="stickyHeader">
        <zm-sticky
          enabled
          check-position="top"
          v-if="showHeader"
          append-cls="zm-table fixed-left"
          :max-top="stickyMaxTop"
          :offset="stickyHeader.offset || 0"
          ref="leftStickyHeader"
        >
          <div
            class="zm-table__fixed-header-wrapper"
            ref="fixedHeaderWrapper">
            <table-header
              ref="fixedTableHeader"
              fixed="left"
              :border="border"
              :store="store"
            ></table-header>
          </div>
        </zm-sticky>
      </template>
      <template v-else>
        <div
          v-if="showHeader"
          class="zm-table__fixed-header-wrapper"
          ref="fixedHeaderWrapper">
          <table-header
            ref="fixedTableHeader"
            fixed="left"
            :border="border"
            :store="store"
          ></table-header>
        </div>
      </template>
      <div
        class="zm-table__fixed-body-wrapper"
        ref="fixedBodyWrapper"
      >
        <table-body
          fixed="left"
          :store="store"
          :stripe="stripe"
          :row-class-name="rowClassName"
          :row-style="rowStyle"
        >
        </table-body>
        <div
          v-if="$slots.append"
          class="zm-table__append-gutter"
          :style="{ height: layout.appendHeight + 'px'}"></div>
      </div>
      <div
        v-if="showSummary"
        v-show="data && data.length > 0"
        class="zm-table__fixed-footer-wrapper"
        ref="fixedFooterWrapper">
        <table-footer
          fixed="left"
          :border="border"
          :sum-text="sumText || t('table.sumText')"
          :summary-method="summaryMethod"
          :store="store"
          :style="{
            width: bodyWidth
          }"></table-footer>
      </div>
    </div>
    <div
      v-if="showFixedRight"
      v-mousewheel="handleFixedMousewheel"
      @mouseenter="onEnterFixedPanel"
      @mouseleave="onLeaveFixedPanel"
      class="zm-table__fixed-right"
      ref="rightFixedWrapper"
    >
      <template v-if="stickyHeader">
        <zm-sticky
          enabled
          v-if="showHeader"
          check-position="top"
          append-cls="zm-table fixed-right"
          :max-top="stickyMaxTop"
          :offset="stickyHeader.offset || 0"
          ref="rightStickyHeader"
        >
          <div
            class="zm-table__fixed-header-wrapper"
            ref="rightFixedHeaderWrapper">
            <table-header
              ref="rightFixedTableHeader"
              fixed="right"
              :border="border"
              :store="store"
            ></table-header>
          </div>
        </zm-sticky>
      </template>
      <template v-else>
        <div
          v-if="showHeader"
          class="zm-table__fixed-header-wrapper"
          ref="rightFixedHeaderWrapper">
          <table-header
            ref="rightFixedTableHeader"
            fixed="right"
            :border="border"
            :store="store"
          ></table-header>
        </div>
      </template>
      <div
        class="zm-table__fixed-body-wrapper"
        ref="rightFixedBodyWrapper"
      >
        <table-body
          fixed="right"
          :store="store"
          :stripe="stripe"
          :row-class-name="rowClassName"
          :row-style="rowStyle"
        >
        </table-body>
        <div
          v-if="$slots.append"
          class="zm-table__append-gutter"
          :style="{ height: layout.appendHeight + 'px' }"></div>
      </div>
      <div
        v-if="showSummary"
        v-show="data && data.length > 0"
        class="zm-table__fixed-footer-wrapper"
        ref="rightFixedFooterWrapper">
        <table-footer
          fixed="right"
          :border="border"
          :sum-text="sumText || t('table.sumText')"
          :summary-method="summaryMethod"
          :store="store"
          :style="{
            width: bodyWidth
          }"></table-footer>
      </div>
    </div>
    <div
      v-if="showFixedRight"
      class="zm-table__fixed-right-patch"
      ref="rightFixedPatch"
      :style="{
        width: '0',
        height: layout.headerHeight + 'px'
      }"></div>
    <div class="zm-table__column-resize-proxy" ref="resizeProxy" v-show="resizeProxyVisible"></div>
    <template v-if="stickyHeader">
      <zm-sticky
        v-if="initDisplayedColumn && data.length>0"
        enabled
        check-position="top"
        append-cls="zm-table"
        :sticky-height="layout.headerHeight"
        :z-index="11"
        :style="{position:'static'}"
        :max-top="stickyMaxTop"
        :offset="stickyHeader.offset || 0"
        ref="settingStickyHeader"
      >
        <zm-tooltip
          :disabled="!initDisplayedColumn.title"
          :content="initDisplayedColumn.title"
          placement="top"
        >
          <div
            class="zm-table__column-setting"
            :style="{
        top: settingTop
      }"
          >
            <zm-button
              tabindex="-1"
              @click="showColumnPanel"
              @keydown.native.down.prevent="showColumnPanel"
              type="icon"
              icon="zm-icon-settings"
              class="zm-table__column-setting-btn"
              ref="settingBtn"
              :aria-label="initDisplayedColumn.title || 'Column setting'"
            ></zm-button>
          </div>
        </zm-tooltip>
      </zm-sticky>
    </template>
    <template v-else>
      <zm-tooltip
        v-if="initDisplayedColumn && data.length>0"
        :disabled="!initDisplayedColumn.title"
        :content="initDisplayedColumn.title"
        placement="top"
      >
        <div
          class="zm-table__column-setting"
          :style="{
        top: settingTop
      }"
        >
          <zm-button
            tabindex="-1"
            @click="showColumnPanel"
            @keydown.native.down.prevent="showColumnPanel"
            type="icon"
            icon="zm-icon-settings"
            class="zm-table__column-setting-btn"
            ref="settingBtn"
            :aria-label="initDisplayedColumn.title || 'Column setting'"
          ></zm-button>
        </div>
      </zm-tooltip>
    </template>
  </div>
</template>

<script type="text/babel">
  import '../../../i18n/table';
  import ZmCheckbox from '../checkbox';
  import ZmButton from '../button';
  import ZmScrollbar from '../scrollbar';
  import ZmSticky from '../sticky';
  import ZmTooltip from '../tooltip';
  import { debounce } from 'throttle-debounce';
  import { addResizeListener, removeResizeListener } from '../../utils/resize-event';
  import Mousewheel from '../../directives/mousewheel';
  import Locale from '../../mixins/locale';
  import { createStore, mapStates } from './store/helper';
  import TableLayout from './table-layout';
  import TableBody from './table-body';
  import TableHeader from './table-header';
  import TableFooter from './table-footer';
  import { parseHeight } from './util';
  import storage from 'good-storage';
  import { _Vue } from './index';
  import ColumnPanel from './column-panel';
  import Grid from './grid';

  let tableIdSeed = 1;

  const EVENT_LAYOUT = 'layout';

  export default {
    name: 'ZmTable',

    mixins: [Locale],

    directives: {
      Mousewheel
    },

    props: {
      data: {
        type: Array,
        default() {
          return [];
        }
      },

      size: String,

      width: [String, Number],

      height: [String, Number],

      maxHeight: [String, Number],

      fit: {
        type: Boolean,
        default: true
      },

      stripe: Boolean,

      border: Boolean,

      rowKey: [String, Function],

      context: {},

      showHeader: {
        type: Boolean,
        default: true
      },

      showSummary: Boolean,

      sumText: String,

      summaryMethod: Function,

      rowClassName: [String, Function],

      rowStyle: [Object, Function],

      cellClassName: [String, Function],

      cellStyle: [Object, Function],

      headerRowClassName: [String, Function],

      headerRowStyle: [Object, Function],

      headerCellClassName: [String, Function],

      headerCellStyle: [Object, Function],

      highlightCurrentRow: Boolean,

      currentRowKey: [String, Number],

      emptyText: String,

      expandRowKeys: Array,

      defaultExpandAll: Boolean,

      defaultSort: Object,

      tooltipEffect: String,

      spanMethod: Function,

      selectOnIndeterminate: {
        type: Boolean,
        default: true
      },

      indent: {
        type: Number,
        default: 16
      },

      treeProps: {
        type: Object,
        default() {
          return {
            hasChildren: 'hasChildren',
            children: 'children'
          };
        }
      },

      lazy: Boolean,

      load: Function,

      minWidth: {
        type: Number,
        default: 500
      },

      initDisplayedColumn: Object,

      stickyBar: Boolean,

      stickyHeader: {
        type: [Boolean, Object],
        default: false
      },

      useFlip: {
        type: Boolean,
        default: true
      }
    },

    components: {
      TableHeader,
      TableFooter,
      TableBody,
      ZmCheckbox,
      ZmButton,
      ZmScrollbar,
      ZmSticky,
      ZmTooltip
    },

    methods: {
      updateFixedRowHeight() {
        const bodyEl = this.$el.querySelector('.zm-table__body-wrapper');
        const headerEl = this.isHeaderSticky ? document.querySelector('.zm-sticky-fixed .zm-table__header-wrapper') : this.$el.querySelector('.zm-table__header-wrapper');
        const fixedLeftBodyEl = this.$el.querySelector('.zm-table__fixed .zm-table__fixed-body-wrapper');
        const fixedLeftHeaderEl = this.isHeaderSticky ? document.querySelector('.zm-sticky-fixed.fixed-left .zm-table__fixed-header-wrapper') : this.$el.querySelector('.zm-table__fixed .zm-table__fixed-header-wrapper');
        const fixedRightBodyEl = this.$el.querySelector('.zm-table__fixed-right .zm-table__fixed-body-wrapper');
        const fixedRightHeaderEl = this.isHeaderSticky ? document.querySelector('.zm-sticky-fixed.fixed-right .zm-table__fixed-header-wrapper') : this.$el.querySelector('.zm-table__fixed-right .zm-table__fixed-header-wrapper');

        const bodyRows = bodyEl && bodyEl.querySelectorAll('tr');
        const fixedLeftBodyRows = fixedLeftBodyEl && fixedLeftBodyEl.querySelectorAll('tr');
        const fixedRightBodyRows = fixedRightBodyEl && fixedRightBodyEl.querySelectorAll('tr');

        const bodyRowCount = bodyRows && bodyRows.length;

        for (let i = 0; i < bodyRowCount; i++) {
          const height = bodyRows[i].getBoundingClientRect().height;
          fixedLeftBodyRows && (fixedLeftBodyRows[i].style.height = `${height}px`);
          fixedRightBodyRows && (fixedRightBodyRows[i].style.height = `${height}px`);
        }

        const headerRows = headerEl && headerEl.querySelectorAll('tr');
        const fixedLeftHeaderRows = fixedLeftHeaderEl && fixedLeftHeaderEl.querySelectorAll('tr');
        const fixedRightHeaderRows = fixedRightHeaderEl && fixedRightHeaderEl.querySelectorAll('tr');

        const headerRowCount = headerRows && headerRows.length;

        for (let i = 0; i < headerRowCount; i++) {
          const height = headerRows[i].getBoundingClientRect().height;
          fixedLeftHeaderRows && (fixedLeftHeaderRows[i].style.height = `${height}px`);
          fixedRightHeaderRows && (fixedRightHeaderRows[i].style.height = `${height}px`);
        }
      },

      makeSureFixedRowHeightRight() {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          this.updateFixedRowHeight();
          if (this.stickyHeader) {
            this.updateSticky();
          }
        }, 1000);
      },

      onStickyChange(isSticky) {
        this.isHeaderSticky = isSticky;
        this.debouncedCreateOrUpdateGrid();
      },

      setCurrentRow(row) {
        this.store.commit('setCurrentRow', row);
      },

      toggleRowSelection(row, selected) {
        this.store.toggleRowSelection(row, selected, false);
        this.store.updateAllSelected();
      },

      toggleRowExpansion(row, expanded) {
        this.store.toggleRowExpansionAdapter(row, expanded);
      },

      clearSelection() {
        this.store.clearSelection();
      },

      clearFilter(columnKeys) {
        this.store.clearFilter(columnKeys);
      },

      clearSort() {
        this.store.clearSort();
      },

      handleMouseLeave() {
        this.store.commit('setHoverRow', null);
        if (this.hoverState) this.hoverState = null;
      },

      updateScrollY() {
        const changed = this.layout.updateScrollY();
        if (changed) {
          this.layout.updateColumnsWidth();
        }
      },

      handleFixedMousewheel(event, data) {
        const bodyWrapper = this.$refs.bar.$refs.wrap;
        if (Math.abs(data.spinY) > 0) {
          const currentScrollTop = bodyWrapper.scrollTop;
          if (data.pixelY < 0 && currentScrollTop !== 0) {
            event.preventDefault();
          }
          if (data.pixelY > 0 && bodyWrapper.scrollHeight - bodyWrapper.clientHeight > currentScrollTop) {
            event.preventDefault();
          }
          bodyWrapper.scrollTop += Math.ceil(data.pixelY / 5);
        } else {
          bodyWrapper.scrollLeft += Math.ceil(data.pixelX / 5);
        }
      },

      handleHeaderFooterMousewheel(event, data) {
        const { pixelX, pixelY } = data;
        if (Math.abs(pixelX) >= Math.abs(pixelY)) {
          this.bodyWrapper.scrollLeft += data.pixelX / 5;
        }
      },

      syncPosition: function({ scrollLeft, scrollTop, offsetWidth, scrollWidth }) {
        const { headerWrapper, footerWrapper, fixedBodyWrapper, rightFixedBodyWrapper } = this.$refs;
        if (headerWrapper) headerWrapper.style.transform = `translate3d(-${scrollLeft}px,0,0)`;
        if (footerWrapper) footerWrapper.style.transform = `translate3d(-${scrollLeft}px,0,0)`;
        if (fixedBodyWrapper) fixedBodyWrapper.style.transform = `translate3d(0,-${scrollTop}px,0)`;
        if (rightFixedBodyWrapper) rightFixedBodyWrapper.style.transform = `translate3d(0,-${scrollTop}px,0)`;
        const { scrollY, gutterWidth } = this.layout;
        const maxScrollLeftPosition = scrollWidth + (scrollY ? gutterWidth : 0) - offsetWidth - 1;
        if (scrollLeft >= maxScrollLeftPosition) {
          this.scrollPosition = 'right';
        } else if (scrollLeft === 0) {
          this.scrollPosition = 'left';
        } else {
          this.scrollPosition = 'middle';
        }
      },

      bindEvents() {
        // this.bodyWrapper.addEventListener('scroll', this.syncPosition, { passive: true });
        if (this.fit) {
          addResizeListener(this.$el, this.resizeListener);
        }
      },

      unbindEvents() {
        // this.bodyWrapper.removeEventListener('scroll', this.syncPosition, { passive: true });
        if (this.fit) {
          removeResizeListener(this.$el, this.resizeListener);
        }
      },

      resizeListener() {
        if (!this.$ready) return;
        let shouldUpdateLayout = false;
        const el = this.$el;
        const { width: oldWidth, height: oldHeight } = this.resizeState;

        const width = el.offsetWidth;
        if (oldWidth !== width) {
          shouldUpdateLayout = true;
        }

        const height = el.offsetHeight;
        if (oldHeight !== height) {
          shouldUpdateLayout = true;
        }

        if (shouldUpdateLayout) {
          this.resizeState.width = width;
          this.resizeState.height = height;
          this.$refs.bar.scrollToBeginX();
          this.debouncedUpdateLayout();
        }
      },

      doLayout() {
        this.layout.updateColumnsWidth();
        this.layout.updateElsHeight();
        clearTimeout(this.updateTimer);
        this.updateTimer = setTimeout(() => {
          if (this.$refs.bar) {
            this.$refs.bar.update();
          }
          this.debouncedCreateOrUpdateGrid();
          this.updateFixedRowHeight();
          if (this.stickyHeader) {
            this.updateSticky();
          }
        }, 10);
        this.$emit(EVENT_LAYOUT, this.layout);
      },

      updateSticky() {
        const stickyRef = ['stickyHeader', 'leftStickyHeader', 'rightStickyHeader', 'settingStickyHeader'];
        stickyRef.forEach((item) => {
          if (this.$refs[item]) {
            this.$refs[item].updateCloneNode();
          }
        });
      },

      sort(prop, order) {
        this.store.commit('sort', { prop, order });
      },

      toggleAllSelection() {
        this.store.commit('toggleAllSelection');
      },

      updateDisplayedColumns() {
        const props = this.displayedProps;
        this.store.states._columns.forEach((column) => {
          if (column.type === 'selection' || column.type === 'expand' || column.type === 'index' || column.fixed) {
            return;
          }
          if (!column.needDisplay && props.indexOf(column.property) !== -1) {
            this.store.commit('showColumn', column.property);
          } else if (column.needDisplay && props.indexOf(column.property) === -1) {
            this.store.commit('hideColumn', column.property);
          }
        });
      },

      showColumnPanel() {
        if (!this.columnPanel) {
          this.columnPanel = new _Vue(ColumnPanel);
          this.columnPanel.table = this;
          this.columnPanel.cell = this.$refs.settingBtn.$el;
          this.columnPanel.displayedProps = this.displayedProps.slice();
          this.columnPanel.ignore = this.initDisplayedColumn.ignore;
          this.columnPanel.$mount(document.createElement('div'));
          this.columnPanel.$on('confirm', (displayedProps) => {
            this.displayedProps = displayedProps;
          });
        } else {
          this.columnPanel.cell = this.$refs.settingBtn.$el;
          this.columnPanel.displayedProps = this.displayedProps.slice();
        }

        setTimeout(() => {
          this.columnPanel.showPopper = true;
        }, 17);
      },

      onEnterFixedPanel() {
        this.$refs.bar.focus();
      },

      onLeaveFixedPanel() {
        this.$refs.bar.blur();
      }
    },

    computed: {
      flip() {
        return this.useFlip && this.store.states.flip;
      },

      stickyMaxTop() {
        return this.layout.tableHeight - this.layout.headerHeight;
      },

      settingTop() {
        return this.store.states.hasSortColumn ? '17px' : '12px';
      },

      tableSize() {
        return this.size || (this.$ELEMENT || {}).size;
      },

      bodyWrapper() {
        return this.$refs.bodyWrapper;
      },

      // shouldUpdateHeight() {
      //   return this.height ||
      //     this.maxHeight ||
      //     this.fixedColumns.length > 0 ||
      //     this.rightFixedColumns.length > 0;
      // },

      headWidth() {
        const { bodyWidth, scrollY, gutterWidth } = this.layout;
        return bodyWidth ? bodyWidth + (scrollY ? gutterWidth : 0) + 'px' : '';
      },

      bodyWidth() {
        const { bodyWidth } = this.layout;
        return bodyWidth ? bodyWidth + 'px' : '';
      },

      bodyHeight() {
        const { headerHeight = 0, bodyHeight, footerHeight = 0, scrollY, gutterWidth } = this.layout;
        if (this.height) {
          return {
            height: this.height === 'auto' ? 'auto' : bodyHeight ? bodyHeight + (scrollY ? gutterWidth : 0) + 'px' : ''
          };
        } else if (this.maxHeight) {
          const maxHeight = parseHeight(this.maxHeight);
          if (typeof maxHeight === 'number') {
            return {
              'max-height': (maxHeight - footerHeight - (this.showHeader ? headerHeight : 0)) + 'px'
            };
          }
        }
        return {};
      },

      fixedBodyHeight() {
        if (this.height) {
          return {
            height: this.layout.fixedBodyHeight ? this.layout.fixedBodyHeight + 'px' : ''
          };
        } else if (this.maxHeight) {
          let maxHeight = parseHeight(this.maxHeight);
          if (typeof maxHeight === 'number') {
            // maxHeight = this.layout.scrollX ? maxHeight + 2 * this.layout.gutterWidth : maxHeight;
            if (this.showHeader) {
              maxHeight -= this.layout.headerHeight;
            }
            maxHeight -= this.layout.footerHeight;
            return {
              'max-height': maxHeight + 'px'
            };
          }
        }
        return {};
      },

      fixedHeight() {
        if (this.maxHeight) {
          return {
            bottom: 0
          };
        } else {
          if (this.showSummary) {
            return {
              height: this.layout.tableHeight ? this.layout.tableHeight + 'px' : ''
            };
          }
          return {
            height: this.layout.viewportHeight ? this.layout.viewportHeight + 'px' : ''
          };
        }
      },

      displayedColumnsKey() {
        if (this.initDisplayedColumn) {
          return `zm-table-${this.initDisplayedColumn.key}`;
        }
        return null;
      },

      showFixedRight() {
        return this.rightFixedColumns.length > 0 && !this.flip && this.layout.scrollX && this.data.length > 0;
      },

      showFixedLeft() {
        return this.fixedColumns.length > 0 && !this.flip && this.layout.scrollX && this.data.length > 0;
      },

      showNotFixedHeader() {
        return this.showHeader && !this.flip && this.data.length > 0;
      },

      ...mapStates({
        selection: 'selection',
        columns: 'columns',
        tableData: 'data',
        fixedColumns: 'fixedColumns',
        rightFixedColumns: 'rightFixedColumns'
      })
    },

    provide() {
      return {
        table: this
      };
    },

    watch: {
      height: {
        immediate: true,
        handler(value) {
          this.layout.setHeight(value);
        }
      },

      maxHeight: {
        immediate: true,
        handler(value) {
          this.layout.setMaxHeight(value);
        }
      },

      currentRowKey: {
        immediate: true,
        handler(value) {
          if (!this.rowKey) return;
          this.store.setCurrentRowKey(value);
        }
      },

      data: {
        immediate: true,
        handler(value) {
          this.store.commit('setData', value);
          this.$nextTick(() => {
            this.debouncedUpdateLayout();
            this.makeSureFixedRowHeightRight();
            this.$refs.bar.updateSticky();
          });
        }
      },

      expandRowKeys: {
        immediate: true,
        handler(newVal) {
          if (newVal) {
            this.store.setExpandRowKeysAdapter(newVal);
          }
        }
      }
    },

    created() {
      this.tableId = 'zm-table_' + tableIdSeed++;
      this.isHeaderSticky = false;
      this.debouncedUpdateLayout = debounce(50, () => this.doLayout());
      this.debouncedCreateOrUpdateGrid = debounce(50, () => {
        if (!this.grid) {
          this.grid = new Grid(this.$el, this.isHeaderSticky);
        } else {
          this.grid.rebuild(this.isHeaderSticky);
        }
      });
    },

    mounted() {
      this.bindEvents();

      if (this.initDisplayedColumn) {
        let { key, props, useStorage = true } = this.initDisplayedColumn;
        if (!key) {
          throw new Error('You need to specify a unique key for the displayed column.');
        }
        if (useStorage) {
          props = storage.get(this.displayedColumnsKey, props || []);
        }

        this.$watch('displayedProps', (newVal) => {
          if (newVal) {
            storage.set(this.displayedColumnsKey, newVal);
            this.$nextTick(() => {
              this.$refs.bar.scrollToBeginX();
              this.updateDisplayedColumns();
            });
          }
        });

        this.displayedProps = props;
      }

      this.store.updateColumns();
      this.doLayout();

      this.resizeState = {
        width: this.$el.offsetWidth,
        height: this.$el.offsetHeight
      };

      // init filters
      this.store.states.columns.forEach(column => {
        if (column.filteredValue && column.filteredValue.length) {
          this.store.commit('filterChange', {
            column,
            values: column.filteredValue,
            silent: true
          });
        }
      });

      this.$ready = true;
    },

    beforeDestroy() {
      if (this.columnPanel) {
        this.columnPanel.showPopper = false;
      }
    },

    destroyed() {
      clearTimeout(this.timer);
      clearTimeout(this.updateTimer);
      this.unbindEvents();
    },

    data() {
      const { hasChildren = 'hasChildren', children = 'children' } = this.treeProps;
      this.store = createStore(this, {
        rowKey: this.rowKey,
        defaultExpandAll: this.defaultExpandAll,
        selectOnIndeterminate: this.selectOnIndeterminate,
        // TreeTable 的相关配置
        indent: this.indent,
        lazy: this.lazy,
        lazyColumnIdentifier: hasChildren,
        childrenColumnName: children
      });
      const layout = new TableLayout({
        store: this.store,
        table: this,
        fit: this.fit,
        showHeader: this.showHeader
      });
      return {
        layout,
        isHidden: false,
        renderExpanded: null,
        resizeProxyVisible: false,
        resizeState: {
          width: null,
          height: null
        },
        displayedProps: [],
        // 是否拥有多级表头
        isGroup: false,
        scrollPosition: 'left'
      };
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";

  @include b(table) {
    position: relative;
    overflow: hidden;
    box-sizing: border-box;
    flex: 1;
    width: 100%;
    max-width: 100%;
    background-color: $--color-white;
    font-size: 14px;
    color: $--color-text-secondary;

    table {
      max-width: none;
    }

    // 数据为空
    @include e(empty-block) {
      min-height: 60px;
      text-align: center;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    @include e(empty-text) {
      // min-height doesn't work in IE10 and IE11 https://github.com/philipwalton/flexbugs#3-min-height-on-a-flex-container-wont-apply-to-its-flex-items
      // set empty text line height up to contrainer min-height as workaround.
      line-height: 60px;
      width: 50%;
      color: $--color-text-secondary;
    }

    // 展开行
    @include e(expand-column) {
      .cell {
        padding: 0;
        text-align: center;
      }
    }

    @include e(expand-icon) {
      position: relative;
      cursor: pointer;
      color: #666;
      font-size: 12px;
      transition: transform 0.2s ease-in-out;
      height: 20px;
      width: 20px;

      @include m(expanded) {
        transform: rotate(90deg);
      }

      > .zm-icon {
        position: absolute;
        left: 50%;
        top: 50%;
        margin-top: -10px;
      }
    }

    @include e(expanded-cell) {
      background-color: $--color-white;

      // 纯属为了增加权重
      &[class*=cell] {
        padding: 20px 50px;
      }

      &:hover {
        background-color: transparent !important;
      }
    }

    @include e(placeholder) {
      display: inline-block;
      width: 20px;
    }

    @include e(append-wrapper) {
      // 避免外边距重合 https://developer.mozilla.org/zh-CN/docs/Web/CSS/CSS_Box_Model/Mastering_margin_collapsing
      overflow: hidden;
    }

    @include m(fit) {
      border-right: 0;
      border-bottom: 0;

      th.gutter, td.gutter {
        border-right-width: 1px;
      }
    }

    @include m(scrollable-x) {
      //.zm-table__body-wrapper {
      //  overflow-x: auto;
      // }
    }

    @include m(scrollable-y) {
      //.zm-table__body-wrapper {
      //  overflow-y: auto;
      //}

      @include e((column-setting)) {
        right: 18px;
      }
    }

    thead {
      color: $--color-text-secondary;

      &.is-group {
        th {
          background: $--background-color-base;
        }
      }

      th {
        font-size: 14px;
        font-weight: 500;
      }
    }

    th, td {
      padding: 8px 0;
      min-width: 0;
      box-sizing: border-box;
      text-overflow: ellipsis;
      vertical-align: middle;
      position: relative;
      text-align: left;

      &.gutter {
        width: 15px;
        border-right-width: 0;
        border-bottom-width: 0;
        padding: 0;
      }

      &.is-hidden {
        > * {
          visibility: hidden;
        }
      }

      a {
        color: $--color-text-primary;
        font-weight: 600;
        &:hover {
          text-decoration: underline;
        }
      }
    }

    @include m(medium) {
      th, td {
        padding: 10px 0;
      }
    }

    @include m(small) {
      font-size: 12px;
      th, td {
        padding: 8px 0;
      }
    }

    @include m(mini) {
      font-size: 12px;
      th, td {
        padding: 6px 0;
      }
    }

    tr {
      background-color: $--color-white;

      input[type="checkbox"] {
        margin: 0;
      }
    }

    th.is-leaf, td {
      border-bottom: $--table-border;
    }

    th.is-sortable {
      cursor: pointer;
    }

    th {
      overflow: hidden;
      user-select: none;
      background-color: $--table-header-background-color;

      &.break {
        .cell {
          word-break: break-word;
        }
      }

      &.ellipsis {
        .cell {
          > span:first-child {
            max-width: 100%;
            display: inline-block;
            vertical-align: middle;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }
        }
      }

      @include when(center) {
        .cell {
          justify-content: center;
        }
      }

      @include when(right) {
        .cell {
          justify-content: flex-end;
        }
      }

      div {
        display: inline-block;
        padding-left: 10px;
        padding-right: 10px;
        line-height: 40px;
        box-sizing: border-box;
        overflow: hidden;
      }

      > .cell {
        position: relative;
        display: flex;
        align-items: center;
        overflow: hidden;
        width: 100%;
        box-sizing: border-box;

        &.highlight {
          color: $--color-primary;
        }
      }

      &.required > div::before {
        display: inline-block;
        content: "";
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #ff4d51;
        margin-right: 5px;
        vertical-align: middle;
      }
    }

    td {
      div {
        box-sizing: border-box;
      }

      &.gutter {
        width: 0;
      }

      @include when(center) {
        text-align: center;
      }

      @include when(right) {
        text-align: right;
      }
    }

    .cell {
      box-sizing: border-box;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 20px;
      padding: 4px 24px 4px 16px;
      outline-offset: -5px;

      &.break {
        word-break: break-word;
      }

      &.ellipsis {
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      &.zm-tooltip {
        white-space: nowrap;
        min-width: 50px;
      }
    }

    // 拥有多级表头
    @include m((group, border)) {
      border: $--table-border;

      @include share-rule(border-pseudo) {
        content: '';
        position: absolute;
        background-color: $--table-border-color;
        z-index: 1;
      }

      // 表格右部伪 border
      &::after {
        @include extend-rule(border-pseudo);
        top: 0;
        right: 0;
        width: 1px;
        height: 100%;
      }
    }

    // 表格底部伪 border，总是有的
    &::before {
      @include extend-rule(border-pseudo);
      left: 0;
      bottom: 0;
      width: 100%;
      height: 1px;
    }

    // table--border
    @include m(border) {
      border-right: none;
      border-bottom: none;

      &.zm-loading-parent--relative {
        border-color: transparent;
      }

      th, td {
        border-right: $--table-border;

        &:first-child .cell {
          padding-left: 10px;
        }
      }

      th.gutter:last-of-type {
        border-bottom: $--table-border;
        border-bottom-width: 1px;
      }

      & th {
        border-bottom: $--table-border;
      }
    }

    @include m(hidden) {
      visibility: hidden;
    }

    @include e((fixed, fixed-right)) {
      position: absolute;
      top: 0;
      left: 0;
      overflow-x: hidden;
      overflow-y: hidden;
      box-shadow: $--table-fixed-box-shadow;
      border-bottom: $--table-border;
    }

    @include e(fixed-right-patch) {
      position: absolute;
      top: -1px;
      right: 0;
      background-color: $--color-white;
      border-bottom: $--table-border;
    }

    @include e(fixed-right) {
      top: 0;
      left: auto;
      right: 0;

      .zm-table__fixed-header-wrapper,
      .zm-table__fixed-body-wrapper,
      .zm-table__fixed-footer-wrapper {
        left: auto;
        right: 0;
      }
    }

    @include e(fixed-header-wrapper) {
      /*position: absolute;*/
      /*left: 0;*/
      /*top: 0;*/
      /*z-index: 5;*/
      position: relative;
      z-index: 1;
    }

    @include e(fixed-footer-wrapper) {
      position: absolute;
      left: 0;
      bottom: 0;
      z-index: 3;

      & tbody td {
        border-top: $--table-border;
        background-color: $--table-row-hover-background-color;
        color: $--table-font-color;
      }
    }

    @include e(fixed-body-wrapper) {
      /*position: absolute;*/
      /*left: 0;*/
      /*top: 37px;*/
      /*z-index: 3;*/
    }

    @include e((header-wrapper, body-wrapper, footer-wrapper)) {
      width: 100%;
    }

    @include e(footer-wrapper) {
      margin-top: -1px;
      td {
        border-top: $--table-border;
      }
    }

    @include e((column-setting)) {
      position: absolute;
      right: 4px;
      z-index: 6;
      background: $--color-white;
      .zm-button--icon {
        color: $--color-text-secondary;;
      }
    }

    @include e((header, body, footer)) {
      table-layout: fixed;
      border-collapse: separate;
    }

    @include e((header-wrapper, footer-wrapper)) {
      & tbody td {
        background-color: $--table-row-hover-background-color;
        color: $--table-font-color;
      }

      &.is-flip {
        display: none;
      }
    }

    @include e(body-wrapper) {
      overflow: hidden;
      position: relative;

      @include when(scrolling-none) {
        ~ .zm-table__fixed,
        ~ .zm-table__fixed-right {
          box-shadow: none;
        }
      }

      @include when(scrolling-left) {
        ~ .zm-table__fixed {
          box-shadow: none;
        }
      }

      @include when(scrolling-right) {
        ~ .zm-table__fixed-right {
          box-shadow: none;
        }
      }

      .zm-table--border {
        @include when(scrolling-right) {
          ~ .zm-table__fixed-right {
            border-left: $--table-border;
          }
        }

        @include when(scrolling-left) {
          ~ .zm-table__fixed {
            border-right: $--table-border;
          }
        }
      }
    }

    .caret-wrapper {
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      width: 24px;
      vertical-align: middle;
      cursor: pointer;
      overflow: initial;
      position: relative;
    }
    .sort-caret-btn {
      background-color: transparent;
      position: relative;
      border: none;
      padding: 0;
      line-height: 16px;
      &:focus {
        outline: thin dotted;
        outline: 5px auto -webkit-focus-ring-color;
        outline-offset: -2px;
      }
      .zm-icon-ascent, .zm-icon-descent {
        float: left;
        font-size: 16px;
      }
    }

    .ascending {
      .zm-icon-ascent {
        color: $--color-primary;
      }
    }

    .descending {
      .zm-icon-descent {
        color: $--color-primary;
      }
    }
    // .sort-caret {
    //     width: 0;
    //     height: 0;
    //     border: solid 5px transparent;
    //     position: absolute;
    //     left: 1px;
    //
    //   &.ascending {
    //       border-bottom-color: $--color-text-placeholder;
    //       top: -3px;
    //     }
    //
    //   &.descending {
    //       border-top-color: $--color-text-placeholder;
    //       bottom: -3px;
    //     }
    //   }
    //
    // .ascending .sort-caret.ascending {
    //     border-bottom-color: $--color-primary;
    //   }
    //
    // .descending .sort-caret.descending {
    //     border-top-color: $--color-primary;
    //   }

    .hidden-columns {
      visibility: hidden;
      position: absolute;
      z-index: -1;
    }

    @include m(striped) {
      & .zm-table__body {
        & tr.zm-table__row--striped {
          td {
            background: #FAFAFA;
          }

          &.current-row td {
            background-color: $--table-current-row-background-color;
          }
        }
      }
    }

    @include e(body) {
      tr.hover-row {
        &, &.zm-table__row--striped {
          &, &.current-row {
            > td {
              background-color: $--table-row-hover-background;
            }
          }
        }
      }

      tr.current-row > td {
        background-color: $--table-current-row-background-color;
      }

      &.is-flip {
        display: block;

        tbody {
          display: block;
        }
      }

      tr.is-flip {
        display: block;
      }

      td.is-flip {
        display: block;
        position: relative;
        /*padding-left: 35%;*/

        .flip-label {
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          -webkit-transform: translateY(-50%);
          padding-left: 4px;
        }
      }
    }

    @include e(column-resize-proxy) {
      position: absolute;
      left: 200px;
      top: 0;
      bottom: 0;
      width: 0;
      border-left: $--table-border;
      z-index: 10;
    }

    @include e(column-filter-trigger) {
      display: inline-block;
      line-height: 34px;
      cursor: pointer;

      & i {
        color: $--color-info;
        font-size: 12px;
        transform: scale(.75);
      }
    }

    @include m(enable-row-transition) {
      .zm-table__body td {
        transition: background-color .25s ease;
      }
    }

    @include m(enable-row-hover) {
      .zm-table__body tr:hover > td {
        background-color: $--table-row-hover-background;
      }
    }

    @include m(fluid-height) {
      .zm-table__fixed,
      .zm-table__fixed-right {
        bottom: 0;
        overflow: hidden;
      }
    }

    [class*=zm-table__row--level] {
      .zm-table__expand-icon {
        display: inline-block;
        width: 20px;
        line-height: 20px;
        height: 20px;
        text-align: center;
        margin-right: 3px;
      }
    }

    @include b(table-column) {
      @include m(selection) {
        .cell {
          padding: 4px;
          .zm-checkbox-wrap {
            vertical-align: middle;
            padding-left: 16px;
            .zm-checkbox__inner {
              top: 1px;
            }
            .zm-checkbox__label {
              position: absolute;
              width: 1px;
              height: 1px;
              padding: 0;
              margin: -1px;
              overflow: hidden;
              clip: rect(0, 0, 0, 0);
              border: 0;
            }
          }
        }
        &.is-flip {
          .cell {
            padding: 4px 24px 4px 16px;
          }
        }
      }
    }
  }

  .zm-sticky-fixed {
    .zm-table__column-setting {
      right: 0;
    }
  }
</style>
